import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Database,
  Download,
  Upload,
  RotateCcw,
  Trash2,
  Plus,
  Calendar,
  HardDrive,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

export default function SuperAdminBackupsPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, data: null });
  const fileInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('superadmin_token');
      
      const response = await fetch(`${backendUrl}/api/superadmin/backups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erreur chargement backups');

      const data = await response.json();
      setRestaurants(data.restaurants);
    } catch (error) {
      console.error('Error loading backups:', error);
      toast.error('Impossible de charger les backups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async (restaurantId, restaurantName) => {
    try {
      setActionLoading(`create-${restaurantId}`);
      const token = localStorage.getItem('superadmin_token');

      const response = await fetch(`${backendUrl}/api/superadmin/backups/${restaurantId}/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erreur création backup');

      const data = await response.json();
      toast.success(`Backup créé : ${data.backup.filename}`);
      
      // Recharger les backups
      loadBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Impossible de créer le backup');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadBackup = async (restaurantId, backupFilename) => {
    try {
      setActionLoading(`download-${restaurantId}-${backupFilename}`);
      const token = localStorage.getItem('superadmin_token');

      const response = await fetch(
        `${backendUrl}/api/superadmin/backups/${restaurantId}/${backupFilename}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Erreur téléchargement');

      // Télécharger le fichier
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backupFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Backup téléchargé');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Impossible de télécharger le backup');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestoreBackup = async () => {
    const { restaurantId, backupFilename, restaurantName } = confirmDialog.data;

    try {
      setActionLoading(`restore-${restaurantId}-${backupFilename}`);
      const token = localStorage.getItem('superadmin_token');

      const response = await fetch(
        `${backendUrl}/api/superadmin/backups/${restaurantId}/${backupFilename}/restore`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Erreur restauration');

      const data = await response.json();
      toast.success(`Backup restauré : ${data.total_documents_restored} documents`);
      
      setConfirmDialog({ open: false, action: null, data: null });
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Impossible de restaurer le backup');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteBackup = async () => {
    const { restaurantId, backupFilename } = confirmDialog.data;

    try {
      setActionLoading(`delete-${restaurantId}-${backupFilename}`);
      const token = localStorage.getItem('superadmin_token');

      const response = await fetch(
        `${backendUrl}/api/superadmin/backups/${restaurantId}/${backupFilename}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Erreur suppression');

      toast.success('Backup supprimé');
      setConfirmDialog({ open: false, action: null, data: null });
      
      // Recharger les backups
      loadBackups();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Impossible de supprimer le backup');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Le fichier doit être un .json');
      return;
    }
    openConfirmDialog('upload', { file, fileName: file.name });
  };

  const handleUploadRestore = async () => {
    const { file } = confirmDialog.data;
    try {
      setActionLoading('upload');
      const token = localStorage.getItem('superadmin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/superadmin/backups/upload-restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Erreur import backup');

      toast.success(`Backup importé et restauré : ${data.total_documents_restored} documents`);
      setConfirmDialog({ open: false, action: null, data: null });
      loadBackups();
    } catch (error) {
      console.error('Error uploading backup:', error);
      toast.error(error.message || "Impossible d'importer le backup");
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmDialog = (action, data) => {
    setConfirmDialog({ open: true, action, data });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSize = (sizeKb) => {
    if (sizeKb < 1024) return `${sizeKb.toFixed(2)} KB`;
    return `${(sizeKb / 1024).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Database className="w-8 h-8" />
            Gestion des Backups
          </h1>
          <p className="text-muted-foreground mt-2">
            Backups automatiques hebdomadaires • Rétention de 4 semaines
          </p>
        </div>
        <div>
          <input
            type="file"
            accept=".json,application/json"
            ref={fileInputRef}
            onChange={handleFileSelected}
            className="hidden"
            data-testid="upload-backup-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={actionLoading === 'upload'}
            data-testid="upload-backup-btn"
          >
            {actionLoading === 'upload' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importer un backup
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Alert>
        <AlertCircle className="w-4 h-4" />
        <AlertDescription>
          Les backups sont créés automatiquement chaque <strong>dimanche à 2h</strong> du matin.
          Seules les <strong>4 dernières semaines</strong> sont conservées.
        </AlertDescription>
      </Alert>

      {/* Restaurants List */}
      <div className="space-y-4">
        {restaurants.map((restaurant) => (
          <Card key={restaurant.restaurant_id} data-testid={`restaurant-${restaurant.restaurant_id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {restaurant.restaurant_name}
                    <Badge variant={restaurant.backups_count > 0 ? 'default' : 'secondary'}>
                      {restaurant.backups_count} backup{restaurant.backups_count > 1 ? 's' : ''}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {restaurant.restaurant_email}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleCreateBackup(restaurant.restaurant_id, restaurant.restaurant_name)}
                  disabled={actionLoading === `create-${restaurant.restaurant_id}`}
                  data-testid={`create-backup-${restaurant.restaurant_id}`}
                >
                  {actionLoading === `create-${restaurant.restaurant_id}` ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Créer backup
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              {restaurant.backups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="w-12 h-12 mx-auto mb-2 opacity-20" />
                  <p>Aucun backup disponible</p>
                  <p className="text-sm">Créez un backup manuel ou attendez le prochain backup automatique</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {restaurant.backups.map((backup) => (
                    <div
                      key={backup.filename}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      data-testid={`backup-${backup.filename}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            {formatDate(backup.date)}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <HardDrive className="w-3 h-3" />
                              {formatSize(backup.size_kb)}
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {backup.document_count} documents
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadBackup(restaurant.restaurant_id, backup.filename)}
                          disabled={actionLoading === `download-${restaurant.restaurant_id}-${backup.filename}`}
                          data-testid={`download-${backup.filename}`}
                        >
                          {actionLoading === `download-${restaurant.restaurant_id}-${backup.filename}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openConfirmDialog('restore', {
                            restaurantId: restaurant.restaurant_id,
                            restaurantName: restaurant.restaurant_name,
                            backupFilename: backup.filename,
                            backupDate: backup.date
                          })}
                          disabled={actionLoading === `restore-${restaurant.restaurant_id}-${backup.filename}`}
                          data-testid={`restore-${backup.filename}`}
                        >
                          {actionLoading === `restore-${restaurant.restaurant_id}-${backup.filename}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openConfirmDialog('delete', {
                            restaurantId: restaurant.restaurant_id,
                            restaurantName: restaurant.restaurant_name,
                            backupFilename: backup.filename
                          })}
                          disabled={actionLoading === `delete-${restaurant.restaurant_id}-${backup.filename}`}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-${backup.filename}`}
                        >
                          {actionLoading === `delete-${restaurant.restaurant_id}-${backup.filename}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: null, data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog.action === 'restore' ? (
                <>
                  <AlertCircle className="w-5 h-5 text-destructive" />
                  Confirmer la restauration
                </>
              ) : confirmDialog.action === 'upload' ? (
                <>
                  <Upload className="w-5 h-5 text-destructive" />
                  Importer et restaurer ce backup ?
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-destructive" />
                  Confirmer la suppression
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'upload' ? (
                <>
                  <div className="space-y-2 mt-4">
                    <p className="font-medium text-foreground">
                      ⚠️ Attention : les données du restaurant contenu dans ce backup seront ÉCRASÉES par celles du fichier :
                    </p>
                    <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                      <p><strong>Fichier :</strong> {confirmDialog.data?.fileName}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Si le restaurant n'existe pas encore dans cet environnement, il sera créé avec toutes ses données (utilisateurs, produits, fournisseurs, commandes…).
                    </p>
                  </div>
                </>
              ) : confirmDialog.action === 'restore' ? (
                <>
                  <div className="space-y-2 mt-4">
                    <p className="font-medium text-foreground">
                      ⚠️ Attention : Cette action va ÉCRASER toutes les données actuelles du restaurant :
                    </p>
                    <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                      <p><strong>Restaurant :</strong> {confirmDialog.data?.restaurantName}</p>
                      <p><strong>Backup :</strong> {formatDate(confirmDialog.data?.backupDate)}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Les données actuelles seront supprimées et remplacées par celles du backup.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-4">
                    Êtes-vous sûr de vouloir supprimer le backup <strong>{confirmDialog.data?.backupFilename}</strong> ?
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Cette action est irréversible.
                  </p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, action: null, data: null })}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={
                confirmDialog.action === 'restore' ? handleRestoreBackup
                : confirmDialog.action === 'upload' ? handleUploadRestore
                : handleDeleteBackup
              }
              disabled={actionLoading !== null}
              data-testid="confirm-dialog-action-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : confirmDialog.action === 'restore' ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : confirmDialog.action === 'upload' ? (
                <Upload className="w-4 h-4 mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {confirmDialog.action === 'restore' ? 'Restaurer'
                : confirmDialog.action === 'upload' ? 'Importer et restaurer'
                : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
